import * as THREE from 'three';
import { TrackRuntime } from '../core/track/runtime';
import { TrainConfig, carOffset, bogieOffsets } from '../core/sim/train';

const CAR_COLORS = [0xd8452f, 0xe0e4e8, 0xe0e4e8, 0xe0e4e8, 0xe0e4e8, 0xe0e4e8, 0xe0e4e8, 0xe0e4e8];

/** Box-and-tube cars in Corkscrew livery; bogies pivot on the centerline so cars articulate. */
export class TrainMesh {
  readonly group = new THREE.Group();
  private cars: THREE.Group[] = [];
  private wheels: THREE.Mesh[][] = [];
  private config: TrainConfig | null = null;
  private wheelSpin = 0;

  constructor() {
    this.group.name = 'train';
  }

  setTrain(config: TrainConfig): void {
    if (this.config && this.config.cars === config.cars && this.config.carLength === config.carLength) {
      this.config = config;
      return;
    }
    this.config = config;
    for (const c of this.cars) this.group.remove(c);
    this.cars = [];
    this.wheels = [];
    for (let i = 0; i < config.cars; i++) {
      const car = this.buildCar(i, config);
      this.cars.push(car);
      this.group.add(car);
    }
  }

  private buildCar(index: number, config: TrainConfig): THREE.Group {
    const g = new THREE.Group();
    const bodyLen = config.carLength * 0.82;
    const color = index === 0 ? CAR_COLORS[0] : 0xe8e9eb;
    const bodyMat = new THREE.MeshStandardMaterial({ color, roughness: 0.35, metalness: 0.25 });
    const trimMat = new THREE.MeshStandardMaterial({ color: 0x2b3138, roughness: 0.6, metalness: 0.3 });

    const body = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.55, bodyLen), bodyMat);
    body.position.y = 0.62;
    body.castShadow = true;
    g.add(body);

    // seat backs, 2 rows × 2 seats
    for (const dz of [-bodyLen * 0.22, bodyLen * 0.28]) {
      const back = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.5, 0.12), trimMat);
      back.position.set(0, 1.05, dz);
      back.castShadow = true;
      g.add(back);
    }
    // nose cone on the lead car
    if (index === 0) {
      const nose = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.75, 0.8, 4), bodyMat);
      nose.rotation.x = -Math.PI / 2;
      nose.rotation.y = Math.PI / 4;
      nose.position.set(0, 0.55, -bodyLen / 2 - 0.35);
      nose.castShadow = true;
      g.add(nose);
    }

    // bogies with wheels
    const wheelMat = new THREE.MeshStandardMaterial({ color: 0x23262a, roughness: 0.5 });
    const carWheels: THREE.Mesh[] = [];
    const [fb, rb] = bogieOffsets(config);
    for (const dz of [-fb, -rb]) {
      const bogie = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.28, 0.6), trimMat);
      bogie.position.set(0, 0.12, -dz);
      g.add(bogie);
      for (const dx of [-0.62, 0.62]) {
        for (const dz2 of [-0.18, 0.18]) {
          const w = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.1, 12), wheelMat);
          w.rotation.z = Math.PI / 2;
          w.position.set(dx, 0.05, -dz + dz2);
          g.add(w);
          carWheels.push(w);
        }
      }
    }
    this.wheels.push(carWheels);
    return g;
  }

  /**
   * Place each car by its two bogies on the rail line: position = bogie
   * midpoint, orientation from the bogie-to-bogie axis + averaged frame up.
   */
  update(runtime: TrackRuntime, headS: number, v: number, dt: number): void {
    if (!this.config) return;
    this.wheelSpin += (v * dt) / 0.16;
    const [fOff, rOff] = bogieOffsets(this.config);
    for (let i = 0; i < this.cars.length; i++) {
      const centerS = headS + carOffset(this.config, i);
      const fFront = runtime.frameAt(runtime.centerline.normalizeS(centerS + fOff));
      const fRear = runtime.frameAt(runtime.centerline.normalizeS(centerS + rOff));

      const front = fFront.railPos;
      const rear = fRear.railPos;
      const mid = front.clone().add(rear).multiplyScalar(0.5);
      const fwd = front.clone().sub(rear).normalize();
      const up = fFront.up.clone().add(fRear.up).normalize();
      const right = new THREE.Vector3().crossVectors(fwd, up).normalize();
      const trueUp = new THREE.Vector3().crossVectors(right, fwd).normalize();

      const car = this.cars[i];
      car.position.copy(mid);
      const m = new THREE.Matrix4().makeBasis(right, trueUp, fwd.clone().negate());
      car.quaternion.setFromRotationMatrix(m);

      for (const w of this.wheels[i]) w.rotation.x = this.wheelSpin;
    }
  }

  /**
   * Seat-level camera pose for the on-ride POV (front car).
   * Orientation is locked to the car quaternion — no look-ahead along the track.
   */
  povPose(): { pos: THREE.Vector3; quaternion: THREE.Quaternion } | null {
    if (!this.config || this.cars.length === 0) return null;
    const car = this.cars[0];
    // Eye height above the seats, slightly forward (car local −Z is forward).
    const eyeLocal = new THREE.Vector3(0, 1.45, -this.config.carLength * 0.28);
    const pos = eyeLocal.applyQuaternion(car.quaternion).add(car.position);
    return { pos, quaternion: car.quaternion.clone() };
  }
}
